import React from 'react';
import { inject, observer } from 'mobx-react';
import { ReactSVG } from 'react-svg';
import clsx from 'classnames';
import { OrderWithMoreBtn } from '@shopee/chatbot-ui';
import { OrderCardType } from '@shopee/chatbot-ui/lib/components/OrderList';
import { OrderType } from '@shopee/chatbot-ui/lib/components/OrderCardWithButton';

import { Intent, IntentParams, OtherFrequentlyAsked } from '@/types/intent';
import { InitStoreType } from '@/types/store/init';
import Msg from '@/components/Msg';
import NewHotQuestion from '@/components/NewHotQuestion';
import API from '@/services';
import {
  IntentType,
  TRACKING_SECTION,
  TRACKING_TARGET,
  TRACKING_MSG_TYPE,
  SELECT_ORDER_TEXT,
  INTENT_HOT_QUESTION_DISPLAY_TYPE,
  HOT_QUESTION_VIEW_MORE_BTN,
  HOT_QUESTION_CHANGE_QUESTIONS_BTN,
} from '@/utils/constants';
import { getContent, shortcutPredictionAvaliable } from '@/utils/tools';
import { getChildInject, bridgeCallFunc, getTrans } from '@/utils/instantLoaded/tools';
import { cdnFile } from '@/utils/cdnFile';
import { TRACKING_TYPE } from '@/utils/tracking/constants';
import { addObserver, removeObserver } from '@/utils/intersectionObserver';
import { trackingHandler } from '@/utils/btnClickHandler';
import { ChatbotUIBasicProps } from '@/types/common';
import UiOptimisationHotQuestionIcon from '@/assets/img/ui-optimisation-recommender/ui-optimisation-hot-question-icon.svg';
import getHqUIOptimisationGroup, { HQ_UI_OPTIMISATION_GROUP } from '@/utils/experiment/getHqUIOptimisation';
import Finger from '@/assets/img/finger.svg';
import Refresh from '@/assets/img/ic_refresh.svg';
import HotQuestionMask from '@/assets/img/hot_question_mask.svg';
import ArrowDown from '@/assets/img/hot_question_down.svg';

import {
  ANIMATION_DELAY,
  ANIMATION_TRANSITION,
  INERTIAL_SLIDE_ACCELERATION,
  INERTIAL_SLIDE_DISTANCE_THRESHOLD,
  INERTIAL_SLIDE_DURATION_THRESHOLD,
  MAX_TURN_PAGES,
  PER_AUTO_PLAY_DURATION,
  PER_PAGE_HOT_QUESTION_NUMBER,
  PER_PAGE_HOT_QUESTION_NUMBER_4,
  PER_PAGE_HOT_QUESTION_NUMBER_5,
  START_PAGE_NUMBER,
  TOTAL_PAGE_NUMBER,
  validSwipe,
} from './constants';
import { oldTaskbotLoadMore, useForbidOrderRedirect, All_BUYER_TITLES } from '../ChatbotUI/PropsProtocol/OrderList';
import { RepeatedSessionType } from '../ChatbotUI/OrderCardWithButton';
import { FEATURE_TOGGLE_TYPE, featureToggle } from '@/utils/featureToggleConfig';
import { isDriver, isFood } from '@/config/core';
import { isDriverAndFoodUIOptimisation as isDriverAndFoodUIOptimisationFunc } from '@/utils/experiment/getDriverAndFoodUIOptimisationExperiment';

import './style.scss';


const PREFIX = 'intent-head';

enum SWIPE_CONTROL {
  default = 0,
  horizontal,
  vertical,
}

interface ShopeeOrderInfo {
  return_items_count: number;
  refund_amount: number;
  return_status: number;
  return_status_desc: string;
  order_status_desc: string;
  total_price: number;
  order_id: string;
  order_status: number;
  order_status_ext: number;
  order_sn: string;
  shop_id: number;
  checkout_id: number;
  uri_key: string;
  title: string;
  image: string;
  price: number;
  specification: string;
  button_type: string;
  button_id: string;
  tracing_number: string;
  delivered_by: string;
  item_count: number;
  is_seller: boolean;
  node_id: string;
  handleCopyClick?: () => void;
}

export interface IntentHotQuestionProps {
  kbBotId: number;
  kbBotName: string;
  msgContent: string;
  hotQuestions: Array<Intent>;
  initStore: InitStoreType;
  agentStore: any;
  sessionStore: any;
  isHistory: boolean;
  order?: ShopeeOrderInfo;
  repeatedSessionType?: RepeatedSessionType;
  sessionId: string;
  dialogueId: string;
  dialogueType: string;
  historySessionId?: string;
  historyDialogueId?: string;
  nodePointId?: string;
  displayType?: number;
  customCallback?: ChatbotUIBasicProps['customCallback'];
  otherFrequentlyAsked?: OtherFrequentlyAsked;
}

export interface IntentHotQuestionState {
  current: number;
  loading: boolean;
  showLoading: boolean;
  reloadParams: any;
  hasVisitedPage: Array<boolean>;
  hasVisitedAnimation: boolean;
  startPointX: number;
  startTransformX: number;
  swipeControl: SWIPE_CONTROL;
  hasMovedDistance: number;
  tabs: any;
  defaultSelected: any;
  perPageHotQuestionNumber: number;
  isOriginIntentHotQuestion: boolean; // 控制组，使用旧样式
  isIntentHotQuestionGroup1: boolean; // 使用 Change Questions 样式
  isIntentHotQuestionGroup2: boolean; // 使用 View More 样式
  isViewMore: boolean; // 是否 view more
  intentHotQuestionGroup1ClickNum: number; // 点击 Change Questions 按钮的次数
}

export enum ButtonType {
  pre,
  next,
}

interface buttonTabProps {
  value: number;
  totalPage: number;
}

// eslint-disable-next-line no-undef
let animationInterval: NodeJS.Timeout;
// eslint-disable-next-line no-undef
let closeAnimation: NodeJS.Timeout;
// eslint-disable-next-line no-undef
let dotsTimeOut: NodeJS.Timeout;

const DotsList = (props: buttonTabProps) => {
  const { value, totalPage } = props;
  const result = [];
  for (let i = 0; i < totalPage; i++) {
    result.push(<div className={clsx('dot', { selected: value === i })} key={i}></div>);
  }
  return result;
};

const getTranslateXFromMatrix = (matrix: string) => {
  const matrixArr = matrix.match(/\((.+?)\)/g)?.[0].split(',');
  return Number(matrixArr?.[4]?.trim());
};

const getDistance = (distance: number, maxDistance: number, hasMovedDistance: number) => {
  return distance > maxDistance - hasMovedDistance
    ? maxDistance - hasMovedDistance
    : distance < -maxDistance - hasMovedDistance
      ? -maxDistance - hasMovedDistance
      : distance;
};

@inject(getChildInject('initStore', 'agentStore', 'sessionStore'))
@observer
class IntentHotQuestion extends React.Component<IntentHotQuestionProps, IntentHotQuestionState> {
  ele: HTMLDivElement | null = null;
  touchStartTime: number = Date.now();
  totalPage = 0;
  viewedIntentIds: number[] = [];
  transformXForInertialSliding = 0;
  startTimeForInertialSliding = 0;
  isMoving = false;
  group1TrackingData = {}; // change question  使用的 tracking data
  group2TrackingData = {}; // view more 使用的 tracking data
  groupTrackingRef = React.createRef<HTMLDivElement>(); // change question 和 view more 上报用的 ref

  constructor(props: IntentHotQuestionProps) {
    super(props);

    const { displayType = INTENT_HOT_QUESTION_DISPLAY_TYPE.CONTROL } = props;
    const isOriginIntentHotQuestion = displayType === INTENT_HOT_QUESTION_DISPLAY_TYPE.CONTROL;
    const isIntentHotQuestionGroup1 = displayType === INTENT_HOT_QUESTION_DISPLAY_TYPE.GROUP1;
    const isIntentHotQuestionGroup2 = displayType === INTENT_HOT_QUESTION_DISPLAY_TYPE.GROUP2;
    const perPageHotQuestionNumber = (() => {
      if (isIntentHotQuestionGroup1) {
        // 4 个  hot question 一页
        return PER_PAGE_HOT_QUESTION_NUMBER_4;
      } else if (isIntentHotQuestionGroup2) {
        // view more 其实只有两页 第一页的 四个 第二页的全部 所以 写成 5 只是为了方便两页包括完
        return PER_PAGE_HOT_QUESTION_NUMBER_5;
      } else {
        return PER_PAGE_HOT_QUESTION_NUMBER;
      }
    })();
    this.state = {
      tabs: All_BUYER_TITLES,
      defaultSelected: 0,
      current: START_PAGE_NUMBER,
      loading: false, // 接口loading
      showLoading: false, // 展示loading
      reloadParams: {},
      hasVisitedPage: [],
      hasVisitedAnimation: Boolean(window.localStorage.getItem('hasVisitedAnimation')),
      startPointX: 0,
      startTransformX: 0,
      swipeControl: SWIPE_CONTROL.default,
      hasMovedDistance: 0,
      isOriginIntentHotQuestion, // 是否保持原来的 hq
      perPageHotQuestionNumber: perPageHotQuestionNumber,
      isIntentHotQuestionGroup1: isIntentHotQuestionGroup1,
      isIntentHotQuestionGroup2: isIntentHotQuestionGroup2,
      isViewMore: false,
      intentHotQuestionGroup1ClickNum: 0,
    };

    const groupTrackingData = {
      if_history: props.isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
      bot_id: props.kbBotId,
      bot_name: props.kbBotName,
      is_faq: false,
    };
    this.group1TrackingData = {
      ...groupTrackingData,
      button_text: getTrans(HOT_QUESTION_CHANGE_QUESTIONS_BTN),
    };
    this.group2TrackingData = {
      ...groupTrackingData,
      button_text: getTrans(HOT_QUESTION_VIEW_MORE_BTN),
    };

    // 恢复场景做的一个兼容处理 如果 sessionStore.intentHotQuestion 为空 就使用 props 传入的值
    if (!props.sessionStore?.intentHotQuestion?.slice?.()?.length) {
      props.sessionStore.UPDATE_INTENT_HOT_QUESTION(props?.hotQuestions);
    }
  }

  // 将HotQuestions分块
  chunkHotQuestionsList = (hotQuestions: Array<Intent>) => {
    if (!hotQuestions.length) {
      return [];
    }
    const hotQuestionsList = [];
    if (this.state.isOriginIntentHotQuestion) {
      // 之前默认的处理方法
      while (hotQuestions.length) {
        hotQuestionsList.push(hotQuestions.splice(0, this.state.perPageHotQuestionNumber));
      }
    } else {
      // 新的处理方法 这里因为四个 hq 为一组 最后一组会;少   需要把第一组的 前三个补充进来
      const hotQuestionsLen = hotQuestions.length;
      const perPageHotQuestionNumber = this.state.perPageHotQuestionNumber;
      let currentIndex = 0;
      for (let i = 0; i < TOTAL_PAGE_NUMBER; i++) {
        const itemList: any[] = [];
        for (let j = 0; j < perPageHotQuestionNumber; j++) {
          itemList.push(hotQuestions[currentIndex]);
          currentIndex++;
          if (currentIndex >= hotQuestionsLen) {
            currentIndex = 0;
          }
        }
        hotQuestionsList.push(itemList);
      }
    }
    return hotQuestionsList;
  };

  formatHotQuestions = (hotQuestions: Array<Intent>, otherFrequentlyAsked?: OtherFrequentlyAsked) => {
    const questions = hotQuestions.map((question) => ({
      id: question.intent_id,
      text: question.hotquestion_text!,
      intentType: question.intent_type!,
      queryCallback: question.query_callback!,
      intentSource: question.intent_source!,
      extraTrackingData: {
        order_id: this.props.order?.order_id || '',
        order_status: this.getOrderStatusText(this.props.repeatedSessionType, this.props.order),
        is_order_predicted: 'true',
      },
    }));

    otherFrequentlyAsked
      && questions.push({
        text: getContent(otherFrequentlyAsked),
        queryCallback: otherFrequentlyAsked.query_callback,
        adhocCategoryName: otherFrequentlyAsked?.adhoc_category_name?.[0],
        isOtherFrequentlyAsked: true,
      } as any);

    return questions;
  };

  // 根据已曝光的Intent刷新intent列表(包括shortcut/hot question/category)
  refreshIntentList = async (nextPage: number) => {
    const { hasVisitedPage } = this.state;
    if (nextPage === START_PAGE_NUMBER || hasVisitedPage[nextPage]) {
      return;
    }
    this.updateViewedIntentIds(nextPage);

    const { initStore: { sessionId }} = this.props;
    const params: IntentParams = {
      session_id: sessionId,
      page: nextPage,
    };

    (API as { popHeadIntent: Function }).popHeadIntent(params);

    hasVisitedPage[nextPage] = true;
    this.setState({ hasVisitedPage });

    this.requestShortcutIntent();
  };

  requestShortcutIntent = async () => {
    if (!shortcutPredictionAvaliable) {
      return;
    }

    const params = {
      intent_type: IntentType.HeadIntent,
      session_id: this.props.initStore.sessionId,
      showed_intents: this.viewedIntentIds.join('-'),
    };
    const { refresh_intents = {}}
      = (await (API as { refreshIntents: Function }).refreshIntents(params, { connectionLimit: 1 }).catch(() => null))
      || {};

    const { intents = [] } = refresh_intents;
    if (intents.length) {
      this.props.initStore.UPDATE_INTENT_PREDICTION_SHORTCUT_IF_ALLOWED(intents);
    }
  };

  updateViewedIntentIds = (pageIndex: number) => {
    const { hasVisitedPage } = this.state;
    if (hasVisitedPage[pageIndex]) {
      return;
    }

    const intentStartIndex = pageIndex * this.state.perPageHotQuestionNumber;
    const pageIntents = this.props.sessionStore.intentHotQuestion
      .slice(intentStartIndex, intentStartIndex + this.state.perPageHotQuestionNumber)
      .map((item: Intent) => item.intent_id);
    this.viewedIntentIds.push(...pageIntents);
  };

  autoPlay = () => {
    if (!this.ele) {
      return;
    }

    const width = this.ele.parentElement?.clientWidth || 0;
    const { current } = this.state;
    const realPage = current % this.totalPage;
    const height = window.getComputedStyle(this.ele.children[current + 1]).getPropertyValue('height');

    this.refreshIntentList(realPage);

    this.ele.style.height = height;
    this.ele.style.transition = '1s';
    this.ele.style.transform = `translateX(${-(current + 1) * width}px)`;

    dotsTimeOut = setTimeout(() => {
      this.setState({ current: current + 1 });
    }, 1000);
  };

  handleAnimation = () => {
    const { hasVisitedAnimation } = this.state;

    if (!hasVisitedAnimation) {
      clearInterval(animationInterval);
      clearTimeout(closeAnimation);

      try {
        window.localStorage.setItem('hasVisitedAnimation', 'true');
      } catch {}

      this.setState({ hasVisitedAnimation: true });
    }
  };

  getInertialSlid = (current: number) => {
    const now = new Date().getTime();
    const distance = current - this.transformXForInertialSliding;
    const duration = now - this.startTimeForInertialSliding;
    if (duration < INERTIAL_SLIDE_DURATION_THRESHOLD && Math.abs(distance) > INERTIAL_SLIDE_DISTANCE_THRESHOLD) {
      const speed = (2 * Math.abs(distance)) / duration;
      return (speed / INERTIAL_SLIDE_ACCELERATION) * (distance < 0 ? -1 : 1);
    }
    return 0;
  };

  touchStart = (ev: any) => {
    if (!this.ele) {
      return;
    }

    let { current } = this.state;
    const { startTransformX, hasMovedDistance } = this.state;
    const length: number = this.ele.children.length;
    const width = this.ele.parentElement?.clientWidth || 0;
    const style = window.getComputedStyle(this.ele);
    const matrix = style.getPropertyValue('transform');
    const offsetX = getTranslateXFromMatrix(matrix) || 0;

    this.handleAnimation();

    if (current === START_PAGE_NUMBER) {
      current = length - 2;
    } else if (current === length - 1) {
      current = 1;
    }

    const translateX = this.isMoving ? offsetX : -current * width;

    this.ele.style.transition = 'none';
    this.ele.style.transform = `translateX(${translateX}px)`;

    this.setState({
      startPointX: ev.touches[ev.touches.length - 1].pageX,
      current,
      startTransformX: translateX,
      hasMovedDistance: ev.touches.length > 1 ? ((translateX - startTransformX) % width) + hasMovedDistance : 0,
    });
    this.transformXForInertialSliding = translateX;
    this.startTimeForInertialSliding = new Date().getTime();
    this.isMoving = true;
  };

  touchMove = (ev: any) => {
    if (!this.ele) {
      return;
    }

    const { startTransformX, startPointX, swipeControl, hasMovedDistance } = this.state;
    const width = this.ele.parentElement?.clientWidth || 0;
    const maxDistance = MAX_TURN_PAGES * width;
    const now = new Date().getTime();

    const realDistanceX = ev.touches[ev.touches.length - 1].pageX - startPointX;
    const distanceX = getDistance(realDistanceX, maxDistance, hasMovedDistance);
    const translateX = startTransformX + distanceX;

    if (
      (Math.abs(distanceX) <= validSwipe && swipeControl !== SWIPE_CONTROL.horizontal)
      || swipeControl === SWIPE_CONTROL.vertical
    ) {
      this.setState({ swipeControl: SWIPE_CONTROL.vertical });
      return;
    }

    if (now - this.startTimeForInertialSliding > INERTIAL_SLIDE_DURATION_THRESHOLD) {
      this.transformXForInertialSliding = translateX;
      this.startTimeForInertialSliding = now;
    }

    this.setState({ swipeControl: SWIPE_CONTROL.horizontal });

    this.ele.style.transition = 'none';
    this.ele.style.transform = `translateX(${translateX}px)`;

    ev.preventDefault();
  };

  touchEnd = (ev: any) => {
    if (!this.ele || ev.touches.length > 0) {
      return;
    }

    const { clientWidth } = this.ele.parentElement as HTMLDivElement;
    const { startTransformX, hasMovedDistance } = this.state;
    const maxDistance = MAX_TURN_PAGES * clientWidth;

    const matrix = window.getComputedStyle(this.ele).getPropertyValue('transform');
    const translateX = getTranslateXFromMatrix(matrix);
    const slideDistance = this.getInertialSlid(translateX);
    const distanceX = getDistance(translateX - startTransformX + slideDistance, maxDistance, hasMovedDistance);
    const newTranslateX = startTransformX + distanceX;

    const currentPage = Math.round(-newTranslateX / clientWidth);
    const height = window.getComputedStyle(this.ele.children[currentPage]).getPropertyValue('height');
    this.refreshIntentList(currentPage % this.totalPage); // TODO: 告诉后台看过这一页

    this.ele.style.height = height;
    this.ele.style.transition = ANIMATION_TRANSITION;
    this.ele.style.transform = `translateX(${-currentPage * clientWidth}px)`;

    this.setState({
      current: currentPage,
      swipeControl: SWIPE_CONTROL.default,
      startTransformX: 0,
      hasMovedDistance: 0,
    });
    this.isMoving = false;
  };

  handleChangeQuestion = () => {
    const { current, intentHotQuestionGroup1ClickNum } = this.state;
    let newCurrent = current + 1;
    if (newCurrent >= TOTAL_PAGE_NUMBER) {
      newCurrent = 0;
    }
    this.refreshIntentList(newCurrent);
    this.setState({ current: newCurrent, intentHotQuestionGroup1ClickNum: intentHotQuestionGroup1ClickNum + 1 });
    trackingHandler({
      trackingname: TRACKING_TYPE.CHANGE_QUESTION,
      trackingdata: this.group1TrackingData,
    });
  };

  handleViewMore = () => {
    this.setState({ isViewMore: true });
    this.refreshIntentList(1); // 直接展示第二页 全部就行
    trackingHandler({
      trackingname: TRACKING_TYPE.VIEW_MORE,
      trackingdata: this.group2TrackingData,
    });
  };

  initHasVisitedPage = () => {
    this.updateViewedIntentIds(START_PAGE_NUMBER);
    const { hasVisitedPage } = this.state;
    hasVisitedPage[START_PAGE_NUMBER] = true;
    this.setState({ hasVisitedPage });
  };

  componentDidMount() {
    this.initHasVisitedPage();
    const { isOriginIntentHotQuestion, isIntentHotQuestionGroup1, isIntentHotQuestionGroup2 } = this.state;

    if (this.ele?.children?.[0]) {
      const height = window.getComputedStyle(this.ele.children[0]).getPropertyValue('height');
      this.ele.style.height = height;
    }

    if (this.totalPage <= 1) {
      return;
    }

    // 新用户自动播放引导动画
    if (!this.state.hasVisitedAnimation) {
      let autoPlayCount = this.totalPage;

      animationInterval = setInterval(() => {
        this.isMoving = true;
        autoPlayCount--;
        this.autoPlay();
        if (autoPlayCount === 0) {
          clearInterval(animationInterval);
          this.isMoving = false;
        }
      }, PER_AUTO_PLAY_DURATION);

      closeAnimation = setTimeout(() => {
        this.setState({ hasVisitedAnimation: true });
        try {
          window.localStorage.setItem('hasVisitedAnimation', 'true');
        } catch {}
      }, PER_AUTO_PLAY_DURATION * autoPlayCount + ANIMATION_DELAY);
    }

    if (isIntentHotQuestionGroup1 || isIntentHotQuestionGroup2) {
      this.requestShortcutIntent(); // 曝光 intent
    }

    if (isOriginIntentHotQuestion) {
      this.ele?.addEventListener('touchstart', this.touchStart, false);

      this.ele?.addEventListener('touchmove', this.touchMove, false);

      this.ele?.addEventListener('touchend', this.touchEnd, false);
    } else {
      addObserver(this.groupTrackingRef.current);
    }
  }

  componentWillUnmount() {
    if (this.totalPage <= 1) {
      return;
    }

    if (this.state.isOriginIntentHotQuestion) {
      this.ele?.removeEventListener('touchstart', this.touchStart, false);

      this.ele?.removeEventListener('touchmove', this.touchMove, false);

      this.ele?.removeEventListener('touchend', this.touchEnd, false);
    } else {
      removeObserver(this.groupTrackingRef.current);
    }
  }

  copyTracingNumber = (value: string | number) => {
    const params = { text: String(value) };
    bridgeCallFunc(() => {
      window.bridgeCallHandler('copyTextToClipboard', params);
    });
  };

  getOrderStatusText = (repeatedSessionType: RepeatedSessionType | undefined, order: any) => {
    if (!order) {
      return '';
    }

    const isOrderRepeatedSession = repeatedSessionType === RepeatedSessionType.Order;
    return isOrderRepeatedSession ? order?.order_status_desc : order?.return_status_desc || order?.order_status_desc;
  };

  render() {
    const {
      msgContent,
      sessionStore,
      isHistory,
      order,
      sessionId,
      dialogueId,
      repeatedSessionType,
      displayType,
      customCallback = {},
      otherFrequentlyAsked,
    } = this.props;
    const {
      current,
      showLoading,
      isOriginIntentHotQuestion,
      isIntentHotQuestionGroup1,
      isIntentHotQuestionGroup2,
      isViewMore,
      intentHotQuestionGroup1ClickNum,
    } = this.state;
    const isDriverAndFoodUIOptimisation = isDriverAndFoodUIOptimisationFunc();
    const useUIOptimisationHotQuestion = getHqUIOptimisationGroup() !== HQ_UI_OPTIMISATION_GROUP.CONTROL || isDriverAndFoodUIOptimisation;
    const isGroup = isIntentHotQuestionGroup1 || isIntentHotQuestionGroup2 || isDriverAndFoodUIOptimisation; // 使用 change question 或者 view more
    const hotQuestions = this.chunkHotQuestionsList(sessionStore.intentHotQuestion.slice()); // MARK
    this.totalPage = hotQuestions.length;
    hotQuestions.length > 1 && hotQuestions.push(hotQuestions[0], hotQuestions[1]);

    const onLoadMore = (loadMoreProps: any) => {
      return oldTaskbotLoadMore(
        loadMoreProps,
        { is_seller: false, sessionId, dialogueId },
        useForbidOrderRedirect(false),
        { orderType: OrderType.prediction, orderCardType: OrderCardType.smallOrder },
      );
    };

    const trackingProps = this.props.order
      ? {
        componentTrackingData: {
          moreOrder: {
            trackingsection: TRACKING_SECTION[TRACKING_TYPE.ORDER_CARD_OTHER_ORDER],
            trackingtarget: TRACKING_TARGET[TRACKING_TYPE.ORDER_CARD_OTHER_ORDER],
            trackingdataBasic: {
              chat_session_id: this.props.isHistory ? this.props.historySessionId : this.props.sessionId || '',
              chat_dialogue_id: this.props.isHistory ? this.props.historyDialogueId : this.props.dialogueId,
              if_history: this.props.isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
              order_id: this.props.order?.order_id,
              order_status: this.getOrderStatusText(this.props.repeatedSessionType, this.props.order),
              is_order_predicted: 'true',
            },
          },
          panelOrder: {
            trackingsection: TRACKING_SECTION.panelOrder,
            trackingtarget: TRACKING_TARGET.panelOrder,
            trackingdataBasic: {
              chat_session_id: isHistory ? this.props.historySessionId : sessionId,
              chat_dialogue_id: isHistory ? this.props.historyDialogueId : dialogueId,
              node_point_id: this.props.nodePointId,
              origin: 'List',
              button_name: getTrans(SELECT_ORDER_TEXT) || '',
              button_text: getTrans(SELECT_ORDER_TEXT),
              if_history: isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
            },
            trackingItemData: (data: any) => ({
              order_status_tab: data?.orderStatusText,
              orderid: data?.orderId,
              order_status: data?.orderStatusText,
            }),
          },
          panelOrderButton: {
            trackingsection: TRACKING_SECTION.orderSelection,
            trackingtarget: TRACKING_TARGET.button,
            trackingdataBasic: {
              chat_session_id: sessionId || '0',
              chat_dialogue_id: dialogueId || '',
              origin: 'List',
              button_name: getTrans(SELECT_ORDER_TEXT),
            },
            trackingItemData: (data: any) => ({ order_status_tab: data?.orderStatusText }),
          },
          closePanel: {
            trackingsection: TRACKING_SECTION[TRACKING_TYPE.TFE_ORDER_LIST_CLOSE_PANEL],
            trackingtarget: TRACKING_TARGET[TRACKING_TYPE.TFE_ORDER_LIST_CLOSE_PANEL],
            trackingdataBasic: {
              chat_session_id: isHistory ? this.props.historySessionId : sessionId,
              chat_dialogue_id: isHistory ? this.props.historyDialogueId : dialogueId,
              node_point_id: this.props.nodePointId,
              if_history: isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
            },
          },
          panelOrderScroll: {
            trackingsection: TRACKING_SECTION[TRACKING_TYPE.TFE_ORDER_LIST_SCROLL_PANEL],
            trackingtarget: TRACKING_TARGET[TRACKING_TYPE.TFE_ORDER_LIST_SCROLL_PANEL],
            trackingdataBasic: {
              chat_session_id: isHistory ? this.props.historySessionId : sessionId,
              chat_dialogue_id: isHistory ? this.props.historyDialogueId : dialogueId,
              node_point_id: this.props.nodePointId,
              if_history: isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
            },
            trackingItemData: (data: any) => ({ tab_name: data?.name }),
          },
          tab: {
            trackingsection: TRACKING_SECTION[TRACKING_TYPE.TFE_ORDER_LIST_TAB],
            trackingtarget: TRACKING_TARGET[TRACKING_TYPE.TFE_ORDER_LIST_TAB],
            trackingdataBasic: {
              chat_session_id: isHistory ? this.props.historySessionId : sessionId,
              chat_dialogue_id: isHistory ? this.props.historyDialogueId : dialogueId,
              node_point_id: this.props.nodePointId,
              if_history: isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
            },
            trackingItemData: (data: any) => ({ tab_name: data?.title?.name }),
          },
        },
      }
      : {};

    const HotQuestionRender = () => {
      const len = sessionStore.intentHotQuestion.length;
      // 一次展示 4 个 总个数是 9 个 点击 change question 之后切换下面四个 循环
      const startNum = (intentHotQuestionGroup1ClickNum * PER_PAGE_HOT_QUESTION_NUMBER_4) % len;
      const endNum = (startNum + PER_PAGE_HOT_QUESTION_NUMBER_4) % len;
      const currentHotQuestions = (() => {
        if (endNum < startNum) {
          return [
            ...sessionStore.intentHotQuestion.slice(startNum),
            ...sessionStore.intentHotQuestion.slice(0, endNum),
          ];
        } else {
          return sessionStore.intentHotQuestion.slice(startNum, endNum);
        }
      })();

      if (featureToggle.getFeatureEnable(FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_INTENT_HOT_QUESTION, displayType as INTENT_HOT_QUESTION_DISPLAY_TYPE)) {
        return (
          <div
            ref={this.groupTrackingRef}
            data-trackingname={TRACKING_TYPE.CHANGE_QUESTION}
            data-trackingdata={JSON.stringify(this.group1TrackingData)}
          >
            <NewHotQuestion
              isHeadIntent
              categoryName="initial"
              loading={showLoading}
              hotQuestions={this.formatHotQuestions(currentHotQuestions, otherFrequentlyAsked)}
              isHistory={isHistory}
              kbBotId={this.props.kbBotId}
              kbBotName={this.props.kbBotName}
              trackingName={order ? TRACKING_TYPE.ORDER_CARD_HOT_QUESTION : undefined}
              customCallback={customCallback}
              useUIOptimisationHotQuestion={useUIOptimisationHotQuestion}
              isMinimiseCategoryUI={true}
            />
          </div>
        );
      } else if (isIntentHotQuestionGroup1) {
        return (
          <div
            ref={this.groupTrackingRef}
            data-trackingname={TRACKING_TYPE.CHANGE_QUESTION}
            data-trackingdata={JSON.stringify(this.group1TrackingData)}
          >
            <NewHotQuestion
              isHeadIntent
              categoryName="initial"
              loading={showLoading}
              hotQuestions={this.formatHotQuestions(currentHotQuestions)}
              isHistory={isHistory}
              kbBotId={this.props.kbBotId}
              kbBotName={this.props.kbBotName}
              trackingName={order ? TRACKING_TYPE.ORDER_CARD_HOT_QUESTION : undefined}
              customCallback={customCallback}
              useUIOptimisationHotQuestion={useUIOptimisationHotQuestion}
            />
          </div>
        );
      } else if (isIntentHotQuestionGroup2) {
        const currentHotQuestions = isViewMore
          ? sessionStore.intentHotQuestion.slice()
          : sessionStore.intentHotQuestion.slice(0, 4);
        return (
          <div
            className="view-more-hot-question"
            ref={this.groupTrackingRef}
            data-trackingname={TRACKING_TYPE.VIEW_MORE}
            data-trackingdata={JSON.stringify(this.group2TrackingData)}
          >
            <NewHotQuestion
              isHeadIntent
              categoryName="initial"
              loading={showLoading}
              hotQuestions={this.formatHotQuestions(currentHotQuestions)}
              isHistory={isHistory}
              kbBotId={this.props.kbBotId}
              kbBotName={this.props.kbBotName}
              trackingName={order ? TRACKING_TYPE.ORDER_CARD_HOT_QUESTION : undefined}
              hotQuestionDisplayType={displayType}
              customCallback={customCallback}
              useUIOptimisationHotQuestion={useUIOptimisationHotQuestion}
            />
          </div>
        );
      } else {
        return (
          <div className="wrap" ref={(ref) => (this.ele = ref)} style={{ width: `${hotQuestions.length * 100}%` }}>
            {hotQuestions.map((hotQuestionsList, index) => (
              <div
                className="hot-questions"
                key={index}
                style={{ width: `${Math.ceil((1 / hotQuestions.length) * 100)}%` }}
              >
                <NewHotQuestion
                  isHeadIntent
                  categoryName="initial"
                  categoryIndex={index % this.totalPage}
                  loading={showLoading}
                  hotQuestions={this.formatHotQuestions(hotQuestionsList)}
                  realIndex={index}
                  isHistory={isHistory}
                  kbBotId={this.props.kbBotId}
                  kbBotName={this.props.kbBotName}
                  trackingName={order ? TRACKING_TYPE.ORDER_CARD_HOT_QUESTION : undefined}
                  customCallback={customCallback}
                />
              </div>
            ))}
          </div>
        );
      }
    };

    const renderFooter = () => {
      const isMinimiseCategoryUI = featureToggle.getFeatureEnable(FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_INTENT_HOT_QUESTION, displayType as INTENT_HOT_QUESTION_DISPLAY_TYPE);

      if (isMinimiseCategoryUI || isIntentHotQuestionGroup1) {
        return (
          <div
            className={clsx('change-questions-container', { 'minimise-category-ui-change-questions-container': isMinimiseCategoryUI })}
            onClick={this.handleChangeQuestion}
          >
            <ReactSVG src={Refresh} className="change-questions-icon" />
            <span className="change-questions-text">{getTrans(HOT_QUESTION_CHANGE_QUESTIONS_BTN)}</span>
          </div>
        );
      } else if (isIntentHotQuestionGroup2 && !isViewMore) {
        return (
          <div className="view-more-container" onClick={this.handleViewMore}>
            <span className="view-more-text">{getTrans(HOT_QUESTION_VIEW_MORE_BTN)}</span>
            <ReactSVG src={ArrowDown} className="view-more-icon" />
          </div>
        );
      } else if (hotQuestions.length > 1 && isOriginIntentHotQuestion) {
        return (
          <div className="dots-container">
            <div className="dots-wrap">
              {DotsList({
                value: current % this.totalPage,
                totalPage: this.totalPage,
              })}
            </div>
          </div>
        );
      }
    };

    const HotQuestionIconRender = () => {
      if (isGroup) {
        if (useUIOptimisationHotQuestion) {
          return <ReactSVG src={UiOptimisationHotQuestionIcon} className="hot-question-mark-ui-optimisation-icon" />;
        }
        return <ReactSVG src={HotQuestionMask} className="hot-question-mark-icon" />;
      }
      return null;
    };

    return (
      <div className={clsx(PREFIX, { 'hot-question-ui-optimisation': useUIOptimisationHotQuestion })}>
        <div
          className={clsx(`${PREFIX}-msg`, {
            'hot-question-group-msg': isGroup,
            'hot-question-ui-optimisation-msg': useUIOptimisationHotQuestion,
            'minimise-category-ui-title': featureToggle.getFeatureEnable(FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_INTENT_HOT_QUESTION, displayType as INTENT_HOT_QUESTION_DISPLAY_TYPE),
          })}
        >
          {HotQuestionIconRender()}
          {(!order || current % this.totalPage !== 0) && <Msg className="message-content" content={msgContent || ''} />}
          {order && current % this.totalPage === 0 && (
            <OrderWithMoreBtn
              {...trackingProps}
              title=""
              onLoadMore={onLoadMore as any}
              disabled={isHistory}
              tabs={this.state.tabs}
              defaultSelected={this.state.defaultSelected}
              order={{
                orderId: order.order_id,
                orderSn: (order as ShopeeOrderInfo).order_sn,
                orderCardType: OrderCardType.orderWithCopy,
                image: cdnFile(order.image),
                price: '',
                title: order.title,
                orderStatusText: this.getOrderStatusText(repeatedSessionType, order),
                showTotalText: true,
                deliverer: order.delivered_by,
                handleCopyClick: () => this.copyTracingNumber((order as ShopeeOrderInfo).order_sn),
              }}
            />
          )}
        </div>

        {/* 该样式只是为了下边框 当 view more 点击过后 下方是没有按钮的 所以需要取消 */}
        <div className={clsx('container', isGroup && !isViewMore ? 'view-more-hot-question-container' : '')}>
          {HotQuestionRender()}
        </div>

        {renderFooter()}

        {/* 新手引导动画 只有原来的 hq 才会触发 新的 hq 类型  view more 等不会触发 */}
        {!this.state.hasVisitedAnimation && hotQuestions.length > 1 && isOriginIntentHotQuestion && (
          <>
            <ReactSVG src={Finger} className="animation-container" />
            <div className="swipe-trace"></div>
          </>
        )}
      </div>
    );
  }
}

export default IntentHotQuestion;
